import { UserManager, WebStorageStateStore } from 'oidc-client-ts'

/**
 * **Anmelden über NetCon.Cloud** (01.09.2026,
 * [E-289](../../../docs/ENTSCHEIDUNGEN.md#e-289)).
 *
 * Der dritte Weg herein, neben Work-Passwort und NetCon-Zugangsdaten. Er kommt **daneben**, er
 * ersetzt nichts: das vorhandene Formular bleibt unverändert, und wer den Knopf nicht drückt,
 * merkt nichts.
 */

/**
 * **Die Client-Kennung – `netcon-nc-work-01`.**
 *
 * Nicht `netcon-work`, wie eine frühe Fassung der Anleitung schrieb. Am 01.09.2026
 * nachgemessen: Keycloak beantwortet die falsche Kennung mit `400 · „Client not found"` – und
 * zwar **wortgleich** mit einer frei erfundenen. Wer den Unterschied nicht kennt, sucht den
 * Fehler eine Stunde bei sich.
 */
const CLIENT_ID = 'netcon-nc-work-01'
const AUSSTELLER = 'https://id.netcon.cloud/realms/netcon'

/** Der Rückweg. Muss auf das Zeichen genau zu dem passen, was im Realm eingetragen ist. */
export const RUECKWEG = '/auth/netcon/callback'

let gemerkt: UserManager | undefined

/**
 * **Träge angelegt.**
 *
 * `new UserManager(...)` liest beim Bauen `location.origin` und legt einen Speicher an. Auf
 * Modulebene liefe das in jedem Test mit, der irgendetwas aus `lib/` importiert – und in einem
 * Testlauf ohne DOM auch dort, wo es nichts zu tun hat. Dieselbe Überlegung wie bei `getAuth()`
 * auf der Serverseite.
 */
export function netconCloud(): UserManager {
  gemerkt ??= new UserManager({
    authority: AUSSTELLER,
    client_id: CLIENT_ID,
    redirect_uri: `${location.origin}${RUECKWEG}`,
    post_logout_redirect_uri: location.origin,
    response_type: 'code',
    /**
     * **Nur diese drei.**
     *
     * `netcon` ist ein **Standard**-Scope des Clients und gilt automatisch; ausdrücklich
     * anfordern darf man nur *optionale*. Wer ihn mitschickt, bekommt
     * `Invalid scopes: openid profile email netcon` – und zwar für die **ganze** Anfrage, nicht
     * nur für diesen einen Scope. Das hat im Portal eine Stunde gekostet.
     */
    scope: 'openid profile email',
    /**
     * **Token in den `sessionStorage`, nicht in den `localStorage`.**
     *
     * Sie werden ohnehin binnen einer Sekunde gegen eine Work-Sitzung eingetauscht und danach
     * weggeworfen. Was länger liegt, als es gebraucht wird, wird irgendwann gefunden.
     */
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    /** Work erneuert nichts: die Sitzung gehört danach Work, nicht Keycloak. */
    automaticSilentRenew: false,
  })
  return gemerkt
}

/** Den Weg nach NetCon.Cloud antreten. PKCE mit S256 macht `oidc-client-ts` von selbst. */
export async function netconCloudAnmelden(): Promise<void> {
  rueckwegMerken()
  await netconCloud().signinRedirect()
}

/**
 * **Die Seite merken, von der die Anmeldung losgeht** (16.10.2026, Z-21).
 *
 * Bis dahin landete jede NetCon-Anmeldung auf „Mein Tag" – auch wenn sie auf einem tiefen Link
 * begann. Aufgefallen an der Freigabe-Seite für Claude (`/claude/freigeben?…`): ohne ihre
 * Adresszeile ist sie nichts, und der Mensch stand nach dem Anmelden auf der Startseite, während
 * claude.ai auf das „Erlauben" wartete. `/` wird nicht vermerkt – dorthin geht es ohnehin.
 */
function rueckwegMerken(): void {
  try {
    const ziel = zielDerAnmeldung()
    if (ziel !== '/' && !ziel.startsWith('/auth/netcon')) sessionStorage.setItem(ZURUECK, ziel)
  } catch {
    /* Ohne Speicher geht es nach „Mein Tag" – wie bisher. */
  }
}

/**
 * **Der Rückweg**: Code gegen Token tauschen, Token gegen eine Work-Sitzung, Token wegwerfen.
 *
 * Gibt die Fehlermeldung zurück, wenn es nicht ging – `null` heißt: angemeldet.
 */
export async function netconCloudAbschliessen(): Promise<string | null> {
  let idToken: string | undefined
  try {
    const nutzer = await netconCloud().signinRedirectCallback()
    idToken = nutzer.id_token
  } catch {
    return 'Der Rückweg von NetCon.Cloud ließ sich nicht abschließen. Versuch es noch einmal.'
  }

  if (!idToken) return 'NetCon.Cloud hat kein Token mitgeschickt.'

  try {
    const antwort = await fetch('/api/auth/sign-in/netcon-cloud', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id_token: idToken }),
    })

    if (!antwort.ok) {
      const text = await antwort.text().catch(() => '')
      try {
        const daten = JSON.parse(text) as { message?: unknown }
        if (typeof daten.message === 'string' && daten.message) return daten.message
      } catch {
        /* Keine JSON-Antwort – dann bleibt es beim Satz unten. */
      }
      return 'Die Anmeldung über NetCon.Cloud wurde abgelehnt.'
    }
    return null
  } finally {
    /**
     * **Der Token wird immer weggeworfen**, auch wenn der Tausch scheiterte. Ein liegen
     * gebliebenes Token nützt niemandem und ist nach einer Stunde ohnehin abgelaufen – aber es
     * liegt eben eine Stunde.
     */
    await netconCloud()
      .removeUser()
      .catch(() => undefined)
  }
}

/* ── Die stille Anmeldung ─────────────────────────────────────────────── */

/**
 * **Wer aus dem Portal kommt, soll nicht klicken müssen** (01.09.2026,
 * [E-290](../../../docs/ENTSCHEIDUNGEN.md#e-290)).
 *
 * Nils: *„sso wäre ja jetzt auch drin wenn ich mich über netcon.cloud anmelde und dann dort auf
 * den work link klicke?"* – ja, aber es kostete einen Klick: Work kennt die Keycloak-Sitzung
 * nicht von sich aus und zeigte deshalb erst die Maske.
 *
 * Der stille Versuch fragt Keycloak mit `prompt=none`, ob es den Menschen kennt. Nachgemessen am
 * 01.09.2026, ohne Sitzung:
 *
 * ```
 * 302 → /auth/netcon/callback?error=login_required&state=…
 * ```
 *
 * Sofort zurück, **keine Maske, keine Interaktion**. Mit Sitzung kommt stattdessen ein `code`.
 *
 * Dass das trägt, hängt an einer Eigenheit der Adressen: `work.netcon.cloud` und
 * `id.netcon.cloud` liegen beide unter `netcon.cloud` und sind für den Browser **same-site** –
 * Keycloaks Sitzungs-Cookie geht deshalb mit. An genau dieser Stelle scheitern solche
 * Konstruktionen sonst.
 */

/** Ein Versuch je Browser-Sitzung – gesetzt **vor** dem Absprung. Siehe {@link stillAnmelden}. */
const VERSUCHT = 'work-sso-versucht'

/**
 * **Wann zuletzt versucht wurde** – die eigentliche Schleifenbremse (01.09.2026,
 * [E-293](../../../docs/ENTSCHEIDUNGEN.md#e-293)).
 *
 * Eine Schleife dreht in **Sekundenbruchteilen**. Ein Zeitfenster von einer halben Minute
 * bricht sie deshalb genauso sicher wie ein Einmal-Vermerk – nur sperrt es nicht den ganzen
 * Tag aus.
 */
const ZULETZT = 'work-sso-zuletzt'

/** Kürzer als jede Schleife, länger als jeder echte Anlauf. */
const SPERRE_MS = 30_000

/**
 * **Der Notausgang.** `?ohne-sso=1` an der Adresse schaltet den stillen Versuch dauerhaft für
 * diese Browser-Sitzung ab.
 *
 * Er steht hier, weil die Endlosschleife die eine Fehlerart dieser Bauform ist. Sollte trotz
 * aller Sicherungen etwas klemmen, muss es einen Weg zur Maske geben, den man jemandem am
 * Telefon nennen kann – ohne Browserdaten zu löschen.
 */
export const NOTAUSGANG = 'ohne-sso'

/** Die Absagen, die Keycloak auf `prompt=none` schickt, wenn niemand angemeldet ist. */
const STILLE_ABSAGE = new Set([
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
])

function merken(schluessel: string): void {
  try {
    sessionStorage.setItem(schluessel, '1')
  } catch {
    /* Privater Modus o. ä. – dann greift die Sperre nicht, aber sie darf nichts kaputtmachen. */
  }
}

function istGemerkt(schluessel: string): boolean {
  try {
    return sessionStorage.getItem(schluessel) === '1'
  } catch {
    /**
     * **Kein Speicher heißt: nicht versuchen.**
     *
     * Ohne `sessionStorage` gäbe es keine Sperre – und ohne Sperre wäre der stille Versuch
     * genau die Endlosschleife, gegen die er abgesichert ist. Die harmlose Richtung ist die
     * Maske mit einem Klick.
     */
    return true
  }
}

/**
 * **Darf jetzt still versucht werden?**
 *
 * Vier Bedingungen, und jede einzelne bricht die Schleife:
 *
 * 1. noch nicht versucht in dieser Browser-Sitzung (außer man kommt aus dem Portal),
 * 2. kein `?ohne-sso=1` an der Adresse,
 * 3. wir stehen **nicht** schon auf dem Rückweg (sonst spränge der Rückweg sich selbst an),
 * 4. `sessionStorage` ist überhaupt benutzbar,
 * 5. wir laufen **nicht** auf einer Rückschleifen-Adresse – dort gibt es kein Portal (E-299).
 */
export function stillMoeglich(): boolean {
  /**
   * **Wo es kein Portal gibt, wird nicht gefragt** (01.09.2026,
   * [E-299](../../../docs/ENTSCHEIDUNGEN.md#e-299)) – die fünfte Sicherung.
   *
   * Der Playwright-Lauf spricht `http://127.0.0.1:3100` an, und dort ist weder Keycloak
   * erreichbar noch ein Portal vorhanden. Der stille Versuch lief trotzdem los, scheiterte am
   * Netz und ließ die Anmeldemaske erst nach der Notbremse erscheinen: **zwei Anläufe und rund
   * fünf Sekunden in jedem CI-Lauf**, für eine Frage, deren Antwort feststeht.
   *
   * Geprüft wird auf **Rückschleifen-Adressen** und nicht auf „ist es `work.netcon.cloud`".
   * Eine Positivliste müsste bei jedem neuen Testsystem nachgezogen werden, und wer das
   * vergisst, sperrt dort den Weg herein aus – ein Fehler, der sich meldet, aber eben doch
   * einer. Die Negativliste trifft genau die Fälle, in denen es das Portal nachweislich nicht
   * gibt: die Entwicklungsmaschine und die Pipeline.
   */
  if (['localhost', '127.0.0.1', '::1', '[::1]'].includes(location.hostname)) return false

  if (location.pathname.startsWith('/auth/netcon')) return false
  if (new URLSearchParams(location.search).has(NOTAUSGANG)) {
    merken(VERSUCHT)
    return false
  }

  /**
   * **Die Zeitsperre gilt immer** – auch für den Weg aus dem Portal. Sie ist die Bremse, die
   * eine Schleife bricht: die dreht in Sekundenbruchteilen und läuft hier auf jeden Fall
   * dagegen.
   */
  if (zuKurzHer()) return false

  /**
   * **Wer aus dem Portal kommt, darf immer** (01.09.2026,
   * [E-293](../../../docs/ENTSCHEIDUNGEN.md#e-293)).
   *
   * Nils: *„ich musste noch auf den Knopf drücken, das hat er nicht automatisch getan."* Sein
   * Tab trug die Sperre noch von einem früheren Versuch – richtig nach der alten Regel, und
   * trotzdem das falsche Ergebnis.
   *
   * Der Fall, der sonst jeden trifft: Work per Lesezeichen öffnen (noch nicht im Portal
   * angemeldet) → stille Absage, Sperre gesetzt → **dann** im Portal anmelden → zurück zu Work
   * → kein Versuch mehr, obwohl es jetzt gerade klappen würde.
   *
   * Kommt die Navigation von `netcon.cloud`, ist die Frage aber beantwortet, **bevor** sie
   * gestellt wird: dort war eben noch jemand angemeldet. Ein Einmal-Vermerk, der diesen Fall
   * mitsperrt, schützt vor nichts und kostet den Klick, um den es geht.
   */
  if (ausDemPortal()) return true

  return !istGemerkt(VERSUCHT)
}

/** Liegt der letzte Versuch weniger als {@link SPERRE_MS} zurück? */
function zuKurzHer(): boolean {
  try {
    const roh = Number(sessionStorage.getItem(ZULETZT))
    return Number.isFinite(roh) && roh > 0 && Date.now() - roh < SPERRE_MS
  } catch {
    /** Ohne Speicher gibt es keine Bremse – dann wird gar nicht erst versucht. */
    return true
  }
}

/**
 * **Kam der Aufruf aus dem Portal?**
 *
 * `document.referrer` trägt bei einer gewöhnlichen Navigation die Herkunft. Geprüft wird der
 * **Ursprung**, nicht ein Textstück: `startsWith('https://netcon.cloud')` träfe auch
 * `https://netcon.cloud.example.org`.
 *
 * Der eigene Ursprung zählt **nicht** – sonst wäre jeder Klick innerhalb von Work eine
 * „Ankunft aus dem Portal", und die Zeitsperre wäre die einzige Bremse.
 */
function ausDemPortal(): boolean {
  if (!document.referrer) return false
  try {
    const her = new URL(document.referrer)
    if (her.origin === location.origin) return false
    return her.hostname === 'netcon.cloud' || her.hostname.endsWith('.netcon.cloud')
  } catch {
    return false
  }
}

/**
 * **Den stillen Versuch antreten.**
 *
 * `merken()` steht **vor** `signinRedirect` und nicht danach: nach dem Absprung läuft kein Code
 * mehr. Stünde die Sperre dahinter, wäre sie nie gesetzt – und das ist die Schleife.
 */
export async function stillAnmelden(): Promise<void> {
  merken(VERSUCHT)
  try {
    sessionStorage.setItem(ZULETZT, String(Date.now()))
  } catch {
    /* Ohne Speicher greift stattdessen `zuKurzHer()`, das dann ohnehin sperrt. */
  }
  rueckwegMerken()
  try {
    await netconCloud().signinRedirect({ prompt: 'none' })
  } catch {
    /* Kein Absprung möglich – dann bleibt die Maske stehen, und das ist die richtige Antwort. */
  }
}

/* ── Das Nachholen nach dem Rückweg ───────────────────────────────────── */

/**
 * **Nach dem Rückweg muss die stille Anmeldung einmal nachgeholt werden** (17.09.2026,
 * [Nachtrag zu E-364](../../../docs/ENTSCHEIDUNGEN.md#e-364)).
 *
 * ## Warum das nicht von selbst passiert
 *
 * `AppShell` fragt still nur, **solange niemand angemeldet ist** – und nach dem Rückweg ist die
 * Work-Sitzung ja gerade entstanden. Der Versuch käme also nie, und das wäre nicht bloß eine
 * fehlende Kopfleiste.
 *
 * ## Warum er trotzdem gebraucht wird – der eigentliche Grund
 *
 * Der Anmeldedienst schickt ein **Back-Channel-Logout nur an Clients, die in dieser
 * Keycloak-Sitzung eine eigene Client-Sitzung haben.** Beim Rückweg meldet sich nur der
 * Portal-Client an; Work durchläuft keinen OIDC-Ablauf und wird beim Abmelden deshalb gar nicht
 * erst gerufen – **egal, was gespeichert ist.** Erst dieser eine `prompt=none`-Durchlauf legt
 * die Client-Sitzung an. Nebenbei liefert er den `sub` aus dem ID-Token, gegen den JWKS geprüft
 * statt aus einer fremden Antwort übernommen.
 *
 * ## Warum nicht einfach `stillMoeglich()`
 *
 * Weil dessen Sperren genau hier zuschnappen könnten, und zwar zu Recht für ihren eigenen Zweck:
 * `VERSUCHT` steht nach einem `?ohne-sso`, und `ZULETZT` steht, wenn Work kurz vorher schon
 * einmal still gefragt hat – wer sich danach binnen 30 Sekunden anmeldet, verlöre den Versuch.
 * Der Nachhol-Versuch hat eine andere Begründung: Er ist **angefordert**, nicht geraten, und der
 * Vermerk wird vor dem Absprung verbraucht. Damit kann er sich nicht wiederholen.
 *
 * Die eine Sperre, die bleibt, ist die Rückschleifen-Adresse: wo es kein Portal gibt
 * ([E-299](../../../docs/ENTSCHEIDUNGEN.md#e-299)), gibt es auch nichts nachzuholen.
 */
const NACHHOLEN = 'work-sso-nachholen'

/** Wohin es nach dem nachgeholten Versuch zurückgehen soll. Siehe {@link zurueckNachAnmeldung}. */
const ZURUECK = 'work-sso-zurueck'

/**
 * **Wohin es nach der Anmeldung zurückgehen soll.**
 *
 * Die Anmeldemaske ersetzt den Inhalt, ohne die Adresse zu ändern – die gewünschte Seite steht
 * also noch da. `ohne-sso` fliegt raus: das ist der einmalige Notausstieg gegen die stille
 * Anmeldung, und mit zurückgetragen wäre er bei jedem Neuladen wieder dabei.
 */
export function zielDerAnmeldung(): string {
  /**
   * Aus den Einzelteilen und nicht über `new URL(location.href)`: Das Ergebnis ist dasselbe,
   * aber es setzt `href` nicht voraus – und damit bleibt die Funktion für einen Test prüfbar,
   * der `location` durch ein schlichtes Objekt ersetzt.
   */
  const suche = new URLSearchParams(location.search)
  suche.delete(NOTAUSGANG)
  const rest = suche.toString()
  const anker = location.hash || ''
  return `${location.pathname}${rest === '' ? '' : `?${rest}`}${anker}`
}

/**
 * **Vor dem Absprung zum Portal vormerken.**
 *
 * Der `sessionStorage` überlebt den Ausflug nach `netcon.cloud` und zurück: Er hängt am Tab und
 * an unserem Ursprung, nicht an der gerade angezeigten Seite.
 */
export function nachholenVormerken(): void {
  try {
    sessionStorage.setItem(NACHHOLEN, '1')
    sessionStorage.setItem(ZURUECK, zielDerAnmeldung())
  } catch {
    /* Ohne Speicher bleibt die Kopfleiste aus – unschön, aber nichts geht kaputt. */
  }
}

/**
 * **Steht ein Nachholen an?** – und wenn ja, ist es damit verbraucht.
 *
 * Das Verbrauchen steht **in** dieser Funktion und nicht beim Aufrufer: Wer sie zweimal fragt,
 * bekommt einmal `true`. Genau das ist die Schleifenbremse, und sie liegt damit an derselben
 * Stelle wie die Frage.
 */
export function nachholenFaellig(): boolean {
  if (['localhost', '127.0.0.1', '::1', '[::1]'].includes(location.hostname)) return false
  if (location.pathname.startsWith('/auth/netcon')) return false
  try {
    if (sessionStorage.getItem(NACHHOLEN) !== '1') return false
    sessionStorage.removeItem(NACHHOLEN)
    return true
  } catch {
    return false
  }
}

/**
 * **Alles bis einschließlich Leerzeichen raus, dazu `DEL`.**
 *
 * Bewusst ohne regulären Ausdruck: Ein Muster mit Steuerzeichen darin ist im Quelltext nicht zu
 * sehen, schlägt bei ESLint (`no-control-regex`) an und wurde beim Schreiben dieser Datei prompt
 * einmal in **rohe** Steuerzeichen verwandelt. Ein Vergleich von Zeichencodes ist an dieser
 * Stelle das Lesbarere – und man kann ihn prüfen, ohne ihn zu entziffern.
 */
function ohneSteuerzeichen(roh: string): string {
  let sauber = ''
  for (const zeichen of roh) {
    const code = zeichen.codePointAt(0) ?? 0
    /** `0x20` ist das Leerzeichen, `0x7f` ist `DEL`. Beides hat in einem Pfad nichts zu suchen. */
    if (code > 0x20 && code !== 0x7f) sauber += zeichen
  }
  return sauber
}

/**
 * **Wohin nach dem nachgeholten Versuch** – oder `null`, wenn es keinen gab.
 *
 * Ohne das landete der Mensch auf „Mein Tag", nachdem der Rückweg ihn eben erst sorgfältig auf
 * seine Seite zurückgebracht hat. Auch dieser Wert wird beim Lesen verbraucht.
 */
export function zurueckNachAnmeldung(): string | null {
  try {
    const roh = sessionStorage.getItem(ZURUECK)
    sessionStorage.removeItem(ZURUECK)
    if (!roh) return null
    /**
     * **Dieselbe Schranke wie auf der Serverseite** (`zielPruefen` in `netcon-cloud-dienst.ts`),
     * einschließlich der Steuerzeichen: `//boese.example` beginnt auch mit `/`, und
     * `/\t/boese.example` wird beim Auflösen genau dazu.
     *
     * Hier schreibt zwar nur Work selbst in den Speicher – aber eine Schranke, die an zwei
     * Stellen unterschiedlich scharf ist, wird an der schwächeren geprüft, sobald jemand die
     * Wege vertauscht.
     */
    const ziel = ohneSteuerzeichen(roh)
    if (!ziel.startsWith('/') || ziel.startsWith('//') || ziel.startsWith('/\\')) return null
    return ziel
  } catch {
    return null
  }
}

/**
 * **Ist das eine stille Absage?** – dann gehört sie niemandem gezeigt.
 *
 * Keycloak antwortet auf `prompt=none` ohne Sitzung mit `error=login_required`. Das ist kein
 * Fehler, sondern die Auskunft „kenne ich nicht" – wer sie als Fehlermeldung sähe, bekäme beim
 * ersten Besuch einen roten Kasten für einen Vorgang, den er nie ausgelöst hat.
 */
export function istStilleAbsage(suche: string): boolean {
  const fehler = new URLSearchParams(suche).get('error')
  return fehler !== null && STILLE_ABSAGE.has(fehler)
}
